import React, { useCallback, useMemo } from "react";
import { useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { List } from "immutable";
import PropTypes from "prop-types";
import { MEMBER_PERMISSIONS } from "~/records/record.project";
import { removeFormDraft } from "~/store/actions/actions.form.jsx";
import { fetchSubtasks } from "~/store/actions/actions.subtasks.jsx";
import { editTask, moveToTrash, rescheduleTask } from "~/store/actions/actions.tasks.jsx";
import { getRawConfigOption } from "~/store/selectors/selector.configOptions.jsx";
import { makeTaskLabelsByProjectSelector } from "~/store/selectors/selector.labels.jsx";
import { getProjectById } from "~/store/selectors/selector.projects.jsx";
import { getTaskById } from "~/store/selectors/selector.tasks.jsx";
import { getOpenDependenciesById } from "~/store/selectors/selector.tasks.jsx";
import {
  getLoggedUser,
  canSeeTimeEstimates,
  makeCanLoggedUserEditLabelsSelector,
} from "~/store/selectors/selector.users.jsx";
import TaskPanel from "./TaskPanel.jsx";
import { getTaskFormDraftKey } from "../../utils/taskFormDraftKey.jsx";
import { useRefetchOnRealtimeFailed } from "../Hooks/useRefetchOnRealtimeFailed.js";

const TaskPanelContainer = ({ id, projectId, ...rest }) => {
  const dispatch = useDispatch();
  const task = useSelector((state) => getTaskById(state, id));
  const project = useSelector((state) => getProjectById(state, projectId));
  const loggedUser = useSelector((state) => getLoggedUser(state));
  const openDependencies = useSelector((state) => getOpenDependenciesById(state, id).toJS());
  const formKey = getTaskFormDraftKey(task.project_id, task.task_list_id, task.id);

  const assigneeIds = useMemo(() => {
    if (task && task.assignee_ids) {
      if (List.isList(task.assignee_ids)) {
        return task.assignee_ids.toArray();
      }
      if (Array.isArray(task.assignee_ids)) {
        return task.assignee_ids;
      }
    }
    return [];
  }, [task]);

  const queryClient = useQueryClient();

  const getTaskLabelsByProjectSelector = useMemo(makeTaskLabelsByProjectSelector, []);

  const labels = useSelector((state) => getTaskLabelsByProjectSelector(state, projectId));

  const getCanLoggedUserAddTaskToPriority = useMemo(makeCanLoggedUserEditLabelsSelector, []);
  const canLoggedUserAddTaskToPriority = useSelector((state) => getCanLoggedUserAddTaskToPriority(state));
  const canSeeEstimates = useSelector((state) => canSeeTimeEstimates(state, projectId));
  const autoReschedule = useSelector((state) => getRawConfigOption(state, "auto_reschedule_enabled"));
  const hideFromClients = useSelector((state) => getRawConfigOption(state, "hide_from_clients_enabled"));

  const refetchSubtasks = useCallback(
    (taskId) => {
      dispatch(fetchSubtasks(projectId, taskId));
    },
    [dispatch, projectId]
  );

  const watch = useRefetchOnRealtimeFailed(refetchSubtasks, "SubtaskUpdated");

  const handleChange = useCallback(
    (attributes) => {
      if (autoReschedule && attributes?.due_on > 0 && task.get("due_on") !== null) {
        watch(task.get(`id`));
        dispatch(rescheduleTask(id, attributes));
      } else {
        dispatch(editTask(projectId, id, attributes));
      }
      dispatch(removeFormDraft(formKey));
    },
    [autoReschedule, task, dispatch, formKey, watch, id, projectId]
  );

  const handleChangeTaskList = useCallback(
    (attributes) => {
      return dispatch(editTask(projectId, id, attributes), removeFormDraft(formKey)).then(() => {
        queryClient.invalidateQueries(["task-page-navigation", projectId, id]);
      });
    },
    [dispatch, formKey, id, projectId, queryClient]
  );

  const handleMoveToTrash = useCallback(() => {
    return dispatch(moveToTrash(id));
  }, [dispatch, id]);

  return (
    <TaskPanel
      {...rest}
      projectId={projectId}
      id={id}
      openDependencies={openDependencies}
      name={task.get("name")}
      onTaskListChange={handleChangeTaskList}
      onChange={handleChange}
      onMoveToTrash={handleMoveToTrash}
      isStrictProject={project?.member_permissions === MEMBER_PERMISSIONS.STRICT}
      taskListId={task.get("task_list_id")}
      assigneeId={task.get("assignee_id")}
      startOn={task.get("start_on")}
      dueOn={task.get("due_on")}
      labels={labels}
      labelIds={task.get("labels")}
      estimate={`${task.get("estimate")}`}
      jobTypeId={task.get("job_type_id")}
      hiddenFromClients={task.get("is_hidden_from_clients")}
      important={task.get("is_important")}
      loggedUserIsClient={loggedUser.isClient()}
      canEdit={task.canEdit(loggedUser, project)}
      canDelete={task.canDelete(loggedUser, project)}
      canSeeTimeEstimates={canSeeEstimates}
      fakeAssigneeName={task.get("fake_assignee_name")}
      canLoggedUserAddTaskToPriority={canLoggedUserAddTaskToPriority}
      hideFromClients={hideFromClients}
      isSplit={task?.is_split}
      assigneeIds={assigneeIds}
    />
  );
};

TaskPanelContainer.displayName = "TaskPanelContainer";

TaskPanelContainer.propTypes = {
  id: PropTypes.number.isRequired,
  projectId: PropTypes.number.isRequired,
};

export default TaskPanelContainer;
